import { Box, Button, Flex, Heading, Text, Icon } from "@chakra-ui/react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useState } from "react";
import heroImagePng from "@/assets/hero-noise.png";
import heroImageAvif from "@/assets/hero-noise.avif";
import { LuArrowRight } from "react-icons/lu";
import { useLandingTheme } from "../Index";
import { useAnalytics } from "@/hooks/useAnalytics";

const MotionBox = motion.create(Box);
const MotionFlex = motion.create(Flex);
const MotionImg = motion.create("img");

type HeroProps = {
  onGetAccess?: () => void;
};

export const Hero = ({ onGetAccess }: HeroProps) => {
  const { theme, isDark } = useLandingTheme();
  const TRACK = useAnalytics();
  const { scrollY } = useScroll();
  const [parallaxAmount, setParallaxAmount] = useState(180);

  useEffect(() => {
    const updateParallax = () => {
      setParallaxAmount(window.innerWidth < 768 ? 80 : 180);
    };
    updateParallax();
    window.addEventListener("resize", updateParallax);
    return () => window.removeEventListener("resize", updateParallax);
  }, []);

  const backgroundY = useTransform(scrollY, [0, 700], [0, parallaxAmount]);

  const scrollToFooter = () => {
    TRACK.BUTTON_CLICKED({ type: "Get Early Access button - hero" });

    if (onGetAccess) {
      onGetAccess();
    } else {
      const footer = document.querySelector("footer");
      footer?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Box
      as="section"
      position="relative"
      pt={{ base: 32, md: 48 }}
      pb={{ base: 20, md: 32 }}
      px={4}
      w="full"
      overflow="hidden"
      id="hero"
    >
      {/* Ambient Background Glow */}
      <Box
        position="absolute"
        top={0}
        left="50%"
        transform="translateX(-50%)"
        w="full"
        h="800px"
        background={`radial-gradient(circle at center, ${theme.primary}${isDark ? "26" : "15"} 0%, transparent 70%)`}
        pointerEvents="none"
        zIndex={0}
      />

      {/* Content */}
      <MotionFlex
        position="relative"
        zIndex={10}
        w="full"
        maxW="6xl"
        mx="auto"
        direction="column"
        align="center"
        textAlign="center"
        gap={8}
        initial="hidden"
        animate="visible"
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: {
              staggerChildren: 0.15,
              delayChildren: 0.1,
            },
          },
        }}
      >
        {/* Beta Badge */}
        <MotionBox
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
          }}
        >
          <Flex
            display="inline-flex"
            align="center"
            gap={2}
            px={4}
            py={2}
            borderRadius="full"
            bg={`${theme.primary}${isDark ? "1a" : "10"}`}
            border="1px solid"
            borderColor={`${theme.primary}30`}
            backdropFilter={isDark ? "blur(4px)" : "none"}
          >
            <Box
              as="span"
              w={2}
              h={2}
              borderRadius="full"
              bg={theme.primary}
              animation="pulse 2s infinite"
              css={{
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                },
              }}
            />
            <Text
              color={theme.primary}
              fontSize="xs"
              fontWeight="semibold"
              letterSpacing="wider"
              textTransform="uppercase"
            >
              Limited beta spots available
            </Text>
          </Flex>
        </MotionBox>

        {/* Main Headline */}
        <MotionBox
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
          }}
        >
          <Heading
            as="h1"
            fontSize={{ base: "5xl", md: "7xl", lg: "8xl" }}
            fontWeight="bold"
            letterSpacing="tighter"
            color={theme.textPrimary}
            lineHeight="1.1"
            maxW="4xl"
          >
            Focus matters.
            <br />
            <Text
              as="span"
              background={
                isDark
                  ? `linear-gradient(to right, white, ${theme.primary}, ${theme.primary}80)`
                  : `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`
              }
              backgroundClip="text"
              color="transparent"
              css={{ WebkitBackgroundClip: "text" }}
            >
              We find it for you!
            </Text>
          </Heading>
        </MotionBox>

        {/* Subheadline with Metrics */}
        <MotionBox
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
          }}
        >
          {/* Metric Stats */}
          <Flex
            direction={{ base: "column", md: "row" }}
            gap={{ base: 4, md: 8 }}
            justify="center"
            align="center"
            mb={6}
          >
            <Flex align="center" gap={3}>
              <Text fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold" color={theme.primary} lineHeight="1">
                5-15
              </Text>
              <Text
                fontSize={{ base: "sm", md: "base" }}
                color={theme.textSecondary}
                fontWeight="medium"
                maxW="120px"
                lineHeight="tight"
                textAlign="left"
              >
                hours reclaimed weekly
              </Text>
            </Flex>

            <Box display={{ base: "none", md: "block" }} h="40px" w="1px" bg={theme.border} />

            <Flex align="center" gap={3}>
              <Text fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold" color={theme.primary} lineHeight="1">
                90+
              </Text>
              <Text
                fontSize={{ base: "sm", md: "base" }}
                color={theme.textSecondary}
                fontWeight="medium"
                maxW="120px"
                lineHeight="tight"
                textAlign="left"
              >
                minute focus blocks, auto-scheduled
              </Text>
            </Flex>
          </Flex>

          {/* Supporting Text */}
          <Text color={theme.textSecondary} fontSize={{ base: "lg", md: "xl" }} maxW="2xl" lineHeight="relaxed">
            Muro automatically carves out deep work sessions in your chaotic calendar.
          </Text>
        </MotionBox>

        {/* CTA Button */}
        <MotionBox
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
          }}
          mt={4}
        >
          <Button
            bg={theme.primary}
            color="white"
            fontSize="base"
            fontWeight="bold"
            px={8}
            py={4}
            h="auto"
            borderRadius="full"
            boxShadow={`0 ${isDark ? "0 20px" : "4px 20px"} ${theme.primaryGlow}`}
            _hover={{
              bg: theme.primaryHover,
              boxShadow: `0 ${isDark ? "0 30px" : "8px 30px"} ${theme.primaryGlow}`,
              transform: "translateY(-2px)",
            }}
            transition="all 0.3s"
            onClick={scrollToFooter}
          >
            <Flex align="center" gap={2}>
              <Text>Get Early Access</Text>
              <Icon as={LuArrowRight} boxSize={4} />
            </Flex>
          </Button>
        </MotionBox>

        {/* Hero Image */}
        <MotionBox
          mt={16}
          w="full"
          position="relative"
          variants={{
            hidden: { opacity: 0, y: 40 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.8, delay: 0.2 } },
          }}
          role="group"
        >
          {/* Glow Effect */}
          <Box
            position="absolute"
            inset={-1}
            background={`linear-gradient(to right, ${theme.primary}${isDark ? "4d" : "30"}, rgba(59, 130, 246, ${isDark ? "0.3" : "0.2"}))`}
            borderRadius="3xl"
            filter="blur(20px)"
            opacity={isDark ? 0.25 : 0.4}
            transition="opacity 1s"
            _groupHover={{ opacity: isDark ? 0.5 : 0.6 }}
          />

          {/* Image Container */}
          <Box
            position="relative"
            w="full"
            aspectRatio={{ base: "16/9", md: "21/9" }}
            borderRadius="3xl"
            overflow="hidden"
            boxShadow={isDark ? "2xl" : "0 25px 50px -12px rgba(0, 0, 0, 0.15)"}
            bg={theme.surface}
            border="1px solid"
            borderColor={theme.border}
          >
            {/* Background Image with Parallax */}
            <MotionBox position="absolute" inset={0} style={{ y: backgroundY }}>
              <picture>
                <source srcSet={heroImageAvif} type="image/avif" />
                <MotionImg
                  src={heroImagePng}
                  alt="Muro productivity dashboard showing focus time blocks scheduled around meetings in a clean calendar interface"
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                  style={{
                    position: "absolute",
                    top: "-40%",
                    left: 0,
                    width: "100%",
                    height: "120%",
                    objectFit: "cover",
                    objectPosition: "center",
                  }}
                />
              </picture>
            </MotionBox>

            {/* Gradient Overlay */}
            <Box
              position="absolute"
              inset={0}
              background={`linear-gradient(to top, ${theme.bg}, transparent 50%, transparent)`}
            />

            {/* Floating UI Element */}
            <Flex
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              p={{ base: 4, md: 12 }}
              align="end"
              justify="space-between"
            >
              <Flex display={{ base: "none", md: "flex" }} direction="column" gap={2}>
                <Flex
                  align="center"
                  gap={2}
                  bg={isDark ? "blackAlpha.600" : "white"}
                  backdropFilter={isDark ? "blur(12px)" : "none"}
                  boxShadow={isDark ? "none" : "lg"}
                  px={4}
                  py={2}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={theme.border}
                >
                  <Box as="span" color={theme.primary} fontSize="lg">
                    ✓
                  </Box>
                  <Text fontSize="sm" fontWeight="medium" color={theme.textPrimary}>
                    Focus mode active
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Box>
        </MotionBox>
      </MotionFlex>
    </Box>
  );
};
